// frontend/js/emotionalDebtMeter.js
import { t, confirmT, updateElementText } from './translationHelper.js';
import translations from './translations.js';
import { apiBaseUrl } from './config.js';

if (!globalThis.ZTranslations) {
  globalThis.ZTranslations = translations;
}

if (typeof globalThis.$t !== 'function') {
  globalThis.$t = (key, params) => {
  return t(key);
  };
}

class EmotionalDebtMeter {
  constructor(containerId, portfolioId) {
    this.container = document.getElementById(containerId);
    this.portfolioId = portfolioId;
    this.data = null;
    this.active = false;
  }

  async init() {
    if (!this.container) return console.warn('[EmotionalDebtMeter] container not found');

    // Initial backend fetch (only once)
    await this.fetchData();

    // Listen for socket updates if available
    if (socket && !this.active) {
      this.active = true;
      this.registerSocketListeners();
    }
  }

  registerSocketListeners() {
    // Emotion or trust index updates from backend
    socket.on('emotionalUpdate', (payload) => {
      if (!payload || payload.portfolioId !== this.portfolioId) return;
      this.updateFromSocket(payload);
    });

    // Trust index change event (optional if separate)
    socket.on('trustIndexUpdate', (payload) => {
      if (!payload || payload.portfolioId !== this.portfolioId) return;
      this.updateFromSocket(payload);
    });
  }

  async fetchData() {
    try {
      const res = await fetch(`/api/social-economy/emotional-state/${this.portfolioId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error('Invalid response');
      this.data = json;
      this.render();
    } catch (err) {
      console.error('[EmotionalDebtMeter] fetch error', err);
      this.container.innerHTML = `<div class="emotional-debt-meter loading"><div class="loading-text">loading emotional state...</div></div>`;
    }
  }

  updateFromSocket(payload) {
    this.data = {
      ...this.data,
      emotionalState: payload.emotionalState ?? this.data?.emotionalState,
      trustIndex: payload.trustIndex ?? this.data?.trustIndex,
      lifeProfitCache: payload.lifeProfitCache ?? this.data?.lifeProfitCache,
      recentTransactions: payload.recentTransactions ?? this.data?.recentTransactions,
    };
    this.render();
  }

  // Convert intensity (0-1) to qualitative description
  getIntensityQualifier(intensity) {
    if (intensity >= 0.9) return { label: 'Overwhelming', color: '#ff00ff' };
    if (intensity >= 0.75) return { label: 'Intense', color: '#ff3366' };
    if (intensity >= 0.6) return { label: 'Strong', color: '#ff6699' };
    if (intensity >= 0.45) return { label: 'Moderate', color: '#00ffcc' };
    if (intensity >= 0.3) return { label: 'Gentle', color: '#66ff99' };
    if (intensity >= 0.15) return { label: 'Faint', color: '#99ff99' };
    return { label: 'Barely Perceptible', color: '#ccffcc' };
  }

  // Convert trust (0-1) to qualitative description
  getTrustQualifier(trust) {
    if (trust >= 0.9) return { label: 'Absolute Faith', color: '#00ffff' };
    if (trust >= 0.75) return { label: 'Deep Trust', color: '#00ddff' };
    if (trust >= 0.6) return { label: 'Solid Confidence', color: '#00bbff' };
    if (trust >= 0.45) return { label: 'Growing Bond', color: '#0099ff' };
    if (trust >= 0.3) return { label: 'Tentative', color: '#ffaa00' };
    if (trust >= 0.15) return { label: 'Fragile', color: '#ff8800' };
    return { label: 'Broken', color: '#ff4444' };
  }

  // Convert life profit to qualitative description
  getLifeProfitQualifier(profit) {
    if (profit >= 500) return { label: 'Radiant Abundance', color: '#00ffff' };
    if (profit >= 300) return { label: 'Flourishing', color: '#00ff99' };
    if (profit >= 150) return { label: 'Thriving', color: '#66ff66' };
    if (profit >= 50) return { label: 'Growing', color: '#99ff99' };
    if (profit >= 10) return { label: 'Emerging', color: '#ccff99' };
    if (profit >= -10) return { label: 'Balanced', color: '#ffff99' };
    if (profit >= -50) return { label: 'Strained', color: '#ffcc66' };
    if (profit >= -150) return { label: 'Burdened', color: '#ff9944' };
    if (profit >= -300) return { label: 'Depleted', color: '#ff6633' };
    if (profit >= -500) return { label: 'Exhausted', color: '#ff4422' };
    return { label: 'Crushing Weight', color: '#ff0000' };
  }

  render() {
    if (!this.data) {
      const loadingText = t('emotional_debt.loading_state', 'loading human capital flux...');
      this.container.innerHTML = `<div class="emotional-debt-meter loading"><div class="loading-text">${loadingText}</div></div>`;
      return;
    }

    const { emotionalState, trustIndex, lifeProfitCache, recentTransactions } = this.data;
    const emotion = emotionalState?.dominant || 'neutral';
    const intensityRaw = (emotionalState?.intensity || 0);
    const trustRaw = (trustIndex || 0);

    // Map emotion → color
    const emotionColor = this.getEmotionColor(emotion);

    // Apply CSS variable for unified tone
    this.container.style.setProperty('--emotion-color', emotionColor);

    // Convert to qualitative descriptions
    const intensityQual = this.getIntensityQualifier(intensityRaw);
    const trustQual = this.getTrustQualifier(trustRaw);
    const lifeProfitQual = this.getLifeProfitQualifier(lifeProfitCache);

    const meterTitle = t('emotional_debt.meter_title', 'Human Capital Flux Index');
    const meterSubtitle = t('emotional_debt.meter_subtitle', 'Relational Topology');
    const trustIndexLabel = t('emotional_debt.trust_index_label', 'Relational Trust');
    const lifeProfitLabel = t('emotional_debt.life_profit_label', 'Existential Balance');
    const synergyDescription = t('emotional_debt.synergy_description', 'moral / emotional / capital synergy');
    const recentEventsTitle = t('emotional_debt.recent_events_title', 'Recent Flux Events');

    this.container.innerHTML = `
      <div class="emotional-debt-meter">
        <div class="meter-header">
          <div class="meter-title">${meterTitle}</div>
          <div class="meter-subtitle">${meterSubtitle}</div>
        </div>

        <div class="meter-body">
          <div class="emotion-display">
            <div class="emotion-icon" style="background: radial-gradient(circle, ${emotionColor} 0%, ${emotionColor}88 50%, transparent 100%); border-color: ${emotionColor};"></div>
            <div class="emotion-info">
              <div class="emotion-name">${this.formatEmotion(emotion)}</div>
              <div class="emotion-intensity">
                <div class="intensity-bar">
                  <div class="intensity-fill" style="width:${Math.round(intensityRaw * 100)}%; background: linear-gradient(90deg, ${emotionColor}dd, ${emotionColor}99);"></div>
                </div>
                <div class="intensity-text" style="color: ${intensityQual.color};">${intensityQual.label}</div>
              </div>
            </div>
          </div>

          <div class="metrics-grid">
            <div class="metric-card">
              <div class="metric-label">${trustIndexLabel}</div>
              <div class="metric-value" style="color: ${trustQual.color}; text-shadow: 0 0 6px ${trustQual.color}88;">
                ${trustQual.label}
              </div>
              <div class="metric-bar">
                <div class="metric-fill trust" style="width:${Math.round(trustRaw * 100)}%; background: linear-gradient(90deg, ${trustQual.color}dd, ${trustQual.color}99);"></div>
              </div>
            </div>
            <div class="metric-card">
              <div class="metric-label">${lifeProfitLabel}</div>
              <div class="metric-value" style="color: ${lifeProfitQual.color}; text-shadow: 0 0 6px ${lifeProfitQual.color}88;">
                ${lifeProfitQual.label}
              </div>
              <div class="metric-description">${synergyDescription}</div>
            </div>
          </div>

          ${recentTransactions?.length
            ? `<div class="recent-transactions">
                <div class="transactions-title">${recentEventsTitle}</div>
                <div class="transactions-list">
                  ${recentTransactions
                    .slice(-5)
                    .map((tx) => this.renderTransaction(tx))
                    .join('')}
                </div>
              </div>`
            : ''}
        </div>
      </div>`;
  }

  renderTransaction(tx) {
    const color = this.getTransactionColor(tx.type);
    const timeAgo = this.timeAgo(new Date(tx.timestamp));
    const partner = tx.from?.name || tx.to?.name || 'unknown';
    const typeKey = `emotional_debt.transaction_types.${tx.type}`;
    const typeLabel = t(typeKey, tx.type);
    const withLabel = t('emotional_debt.transaction_with', 'with');
    return `
      <div class="transaction-item" style="border-left-color:${color}">
        <div class="tx-text">${typeLabel} ${withLabel} ${partner}</div>
        <div class="tx-time">${timeAgo}</div>
      </div>`;
  }

  formatEmotion(emotion) {
    if (!emotion) return t('emotional_debt.emotions.neutral', 'Neutral');
    const emotionKey = `emotional_debt.emotions.${emotion}`;
    const fallback = emotion.charAt(0).toUpperCase() + emotion.slice(1);
    return t(emotionKey, fallback);
  }

  getEmotionColor(emotion) {
    const map = {
      hopeful: '#00ff66',
      anxious: '#ff8800',
      despairing: '#555555',
      resentful: '#ff3333',
      grateful: '#66ff99',
      numb: '#999999',
      altruistic: '#33ffee',
      indebted: '#00ffee',
      empowered: '#00bfff',
      rejected: '#cc00ff',
      relieved: '#00ffcc',
      neutral: '#00ff00',
    };
    return map[emotion] || '#00ff00';
  }

  getTransactionColor(type) {
    const colors = {
      help: '#00ff66',
      loan: '#00bfff',
      forgiveness: '#66ff99',
      betrayal: '#ff3333',
    };
    return colors[type] || '#00ff00';
  }

  timeAgo(date) {
    const seconds = Math.floor((Date.now() - date) / 1000);
    if (seconds < 60) return t('emotional_debt.time_labels.now', 'now');
    const m = Math.floor(seconds / 60);
    if (m < 60) return t('emotional_debt.time_labels.minutes_ago', '{m}m ago').replace('{m}', m);
    const h = Math.floor(m / 60);
    if (h < 24) return t('emotional_debt.time_labels.hours_ago', '{h}h ago').replace('{h}', h);
    const d = Math.floor(h / 24);
    return t('emotional_debt.time_labels.days_ago', '{d}d ago').replace('{d}', d);
  }
}

export default EmotionalDebtMeter;

// =======================================================
// USAGE EXAMPLE (anywhere after portfolio load)
// =======================================================
// import EmotionalDebtMeter from './emotionalDebtMeter.js';
// const meter = new EmotionalDebtMeter('emotionalDebtContainer', currentPortfolioId);
// meter.init();
